import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChapterTag, MaskedLines, FadeUp } from "@/components/Reveal";
import { Arch, PulseGlow } from "@/components/Motifs";

const HERO_LINES = ["Have one", "meaningful", "conversation", "without swiping,", "scrolling, or", "performing."];

export default function Doorway() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const { scrollYProgress: drift } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(p, [0, 0.4], [0.55, 1]);
  const y = useTransform(p, [0, 0.4], [110, 0]);
  const glow = useTransform(p, [0.08, 0.45], [0, 0.55]);
  const r1y = useTransform(drift, [0, 1], [50, -50]);
  const r2y = useTransform(drift, [0, 1], [90, -90]);
  const r3y = useTransform(drift, [0, 1], [30, -30]);

  return (
    <section id="scene-doorway" ref={ref} className="th-scene" style={{ height: "180vh" }} data-testid="scene-doorway">
      <div className="th-sticky">
        <ChapterTag index="02" name="The Doorway" />
        <div className="h-full max-w-[1400px] mx-auto grid lg:grid-cols-2 items-center gap-8 px-[7vw]">
          <div className="relative z-10 order-2 lg:order-1 text-center lg:text-left">
            <p className="th-wordmark-inline" data-testid="threshold-wordmark">
              THRESHOLD
            </p>
            <MaskedLines
              className="th-hero-lines"
              lines={HERO_LINES}
            />
            <FadeUp delay={0.9} data-testid="hero-sub">
              <p className="th-sub">Guided conversation for intentional human connection.</p>
            </FadeUp>
          </div>
          <div className="relative order-1 lg:order-2 h-[42vh] lg:h-[80vh] flex items-center justify-center">
            <motion.div className="th-doorglow" style={{ opacity: glow }} />
            <div className="th-lightspill" aria-hidden="true" />
            <div className="th-beam" style={{ left: "56%", opacity: 0.35 }} aria-hidden="true" />
            <svg className="th-doorhill" viewBox="0 0 1440 220" preserveAspectRatio="none" aria-hidden="true">
              <path d="M0 150 C 260 80, 520 190, 820 130 S 1260 70, 1440 160 V220 H0 Z" fill="var(--sage)" opacity=".14" />
            </svg>
            <motion.div className="absolute" style={{ y: r3y }}>
              <Arch className="w-[38rem] max-w-none opacity-[.08]" sw={1} />
            </motion.div>
            <motion.div className="absolute" style={{ y: r2y }}>
              <Arch className="w-[30rem] max-w-none opacity-[.14]" sw={1} />
            </motion.div>
            <motion.div className="absolute" style={{ y: r1y }}>
              <Arch className="w-[22rem] max-w-none opacity-[.28]" sw={1} />
            </motion.div>
            <motion.div className="th-doorframe" style={{ scale, y }} data-testid="doorway-frame">
              <img src="/assets/img3295.webp" alt="Threshold — Have one meaningful conversation" loading="eager" decoding="async" fetchPriority="high" />
            </motion.div>
            <PulseGlow
              size={7}
              className="absolute"
              style={{ left: "50%", top: "9%", marginLeft: -3, opacity: 0.55, zIndex: 20 }}
            />
          </div>
        </div>
        <div className="th-scrollhint" data-testid="scroll-hint">
          <span>SCROLL</span>
          <i />
        </div>
      </div>
    </section>
  );
}
